// src/const.ts
var GRID_COLS = 17;
var GRID_ROWS = 10;
var TIMER_MS = 12e4;
var TIMER_SECONDS = 120;
var CELL_COUNT = GRID_COLS * GRID_ROWS;
var MIN_VALUE = 1;
var MAX_VALUE = 9;
var TARGET_SUM = 10;

// src/seed.ts
function dateToSeed(yyyymmddKst) {
  let hash = 2166136261;
  for (let i = 0; i < yyyymmddKst.length; i++) {
    hash ^= yyyymmddKst.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}
function mulberry32(seed) {
  let state = seed >>> 0;
  return function next() {
    state = state + 1831565813 >>> 0;
    let t = state;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

// src/grid.ts
var VALUE_SPAN = MAX_VALUE - MIN_VALUE + 1;
var CELL_TOTAL = GRID_ROWS * GRID_COLS;
var MAX_REJECTION_ATTEMPTS = 200;
function generateBoard(seed) {
  const rand = mulberry32(seed);
  let values = null;
  for (let attempt = 0; attempt < MAX_REJECTION_ATTEMPTS; attempt++) {
    const candidate = new Array(CELL_TOTAL);
    let total = 0;
    for (let i = 0; i < CELL_TOTAL; i++) {
      const v = MIN_VALUE + Math.floor(rand() * VALUE_SPAN);
      candidate[i] = v;
      total += v;
    }
    if (total % TARGET_SUM === 0) {
      values = candidate;
      break;
    }
  }
  if (values === null) {
    values = new Array(CELL_TOTAL);
    let total = 0;
    for (let i = 0; i < CELL_TOTAL; i++) {
      const v = MIN_VALUE + Math.floor(rand() * VALUE_SPAN);
      values[i] = v;
      total += v;
    }
    let remainder = total % TARGET_SUM;
    for (let i = values.length - 1; remainder > 0 && i >= 0; i--) {
      const v = values[i];
      const take = Math.min(v - MIN_VALUE, remainder);
      if (take > 0) {
        values[i] = v - take;
        remainder -= take;
      }
    }
    if (remainder !== 0) {
      throw new Error(
        "invariant: tail-shave fallback incomplete \u2014 board generator config (GRID_COLS/ROWS, value range) may be inconsistent"
      );
    }
  }
  const rows = new Array(GRID_ROWS);
  for (let r = 0; r < GRID_ROWS; r++) {
    const row = new Array(GRID_COLS);
    for (let c = 0; c < GRID_COLS; c++) {
      const value = values[r * GRID_COLS + c];
      row[c] = { value, id: `r${r}c${c}` };
    }
    rows[r] = row;
  }
  return rows;
}
function rectSum(grid, rect) {
  const { c1, r1, c2, r2 } = rect;
  const rMin = Math.max(0, Math.min(r1, r2));
  const rMax = Math.min(GRID_ROWS - 1, Math.max(r1, r2));
  const cMin = Math.max(0, Math.min(c1, c2));
  const cMax = Math.min(GRID_COLS - 1, Math.max(c1, c2));
  let sum = 0;
  for (let r = rMin; r <= rMax; r++) {
    const row = grid[r];
    for (let c = cMin; c <= cMax; c++) {
      const cell = row[c];
      if (cell) sum += cell.value;
    }
  }
  return sum;
}
function isRectValid(grid, rect) {
  const { c1, r1, c2, r2 } = rect;
  if (c1 > c2 || r1 > r2) {
    return { valid: false, sum: 0, cleared: 0, reason: "NOT_RECTANGULAR" };
  }
  if (c1 < 0 || r1 < 0 || c2 >= GRID_COLS || r2 >= GRID_ROWS) {
    return { valid: false, sum: 0, cleared: 0, reason: "OUT_OF_BOUNDS" };
  }
  let sum = 0;
  let cleared = 0;
  for (let r = r1; r <= r2; r++) {
    const row = grid[r];
    for (let c = c1; c <= c2; c++) {
      const cell = row[c];
      if (cell) {
        sum += cell.value;
        cleared++;
      }
    }
  }
  if (cleared === 0) {
    return { valid: false, sum: 0, cleared: 0, reason: "EMPTY_REGION" };
  }
  if (sum !== TARGET_SUM) {
    return { valid: false, sum, cleared, reason: "SUM_MISMATCH" };
  }
  return { valid: true, sum, cleared };
}

// src/game.ts
var TIME_EXPIRED_GRACE_MS = 5e3;
function createGame(seed, startedAtMs) {
  return {
    grid: generateBoard(seed),
    score: 0,
    startedAtMs,
    ended: false
  };
}
function isExpired(state, nowMs) {
  return nowMs - state.startedAtMs >= TIMER_MS;
}
function applyMove(state, move, nowMs) {
  if (state.ended) {
    return { ok: false, reason: "GAME_ENDED" };
  }
  if (nowMs - state.startedAtMs > TIMER_MS + TIME_EXPIRED_GRACE_MS) {
    return { ok: false, reason: "TIME_EXPIRED" };
  }
  const validation = isRectValid(state.grid, move.rect);
  if (!validation.valid) {
    return { ok: false, reason: validation.reason };
  }
  const { c1, r1, c2, r2 } = move.rect;
  const nextRows = new Array(GRID_ROWS);
  for (let r = 0; r < GRID_ROWS; r++) {
    const row = state.grid[r];
    if (r < r1 || r > r2) {
      nextRows[r] = row;
      continue;
    }
    const newRow = new Array(GRID_COLS);
    for (let c = 0; c < GRID_COLS; c++) {
      const cell = row[c];
      if (c >= c1 && c <= c2 && cell) {
        newRow[c] = null;
      } else {
        newRow[c] = cell;
      }
    }
    nextRows[r] = newRow;
  }
  return {
    ok: true,
    state: {
      grid: nextRows,
      score: state.score + validation.cleared,
      startedAtMs: state.startedAtMs,
      ended: state.ended
    },
    cleared: validation.cleared
  };
}

// src/replay.ts
function replay(seed, startedAtMs, moves) {
  let state = createGame(seed, startedAtMs);
  let validMoves = 0;
  const rejections = [];
  const perMoveServerScore = [];
  let divergenceIndex = null;
  let prevTsMs = -1;
  for (let i = 0; i < moves.length; i++) {
    const move = moves[i];
    if (move.tsMs < prevTsMs) {
      rejections.push({ move, reason: "NON_MONOTONIC_TS", index: i });
      if (divergenceIndex === null) divergenceIndex = i;
      continue;
    }
    prevTsMs = move.tsMs;
    const nowMs = startedAtMs + move.tsMs;
    const result = applyMove(state, move, nowMs);
    if (result.ok) {
      state = result.state;
      validMoves++;
      perMoveServerScore.push(state.score);
    } else {
      rejections.push({ move, reason: result.reason, index: i });
      if (divergenceIndex === null) divergenceIndex = i;
    }
  }
  return {
    finalState: state,
    validMoves,
    rejections,
    divergenceIndex,
    perMoveServerScore
  };
}

// src/garbage.ts
var TIERS = [
  { minCleared: 10, lines: 3 },
  { minCleared: 6, lines: 2 },
  { minCleared: 3, lines: 1 }
];
function garbageLinesFromCleared(cleared) {
  if (cleared < 0) return 0;
  for (const tier of TIERS) {
    if (cleared >= tier.minCleared) return tier.lines;
  }
  return 0;
}
function pickGarbageTarget(strategy, candidates, rng) {
  const eligible = candidates.filter((c) => c.eligible);
  if (eligible.length === 0) return null;
  switch (strategy) {
    case "attackers": {
      const attackers = eligible.filter((c) => c.targetingMe);
      const pool = attackers.length > 0 ? attackers : eligible;
      return pickRandom(pool, rng);
    }
    case "kos": {
      const minLive = Math.min(...eligible.map((c) => c.liveCells));
      const lowest = eligible.filter((c) => c.liveCells === minLive);
      return pickRandom(lowest, rng);
    }
    case "badges": {
      const maxKos = Math.max(...eligible.map((c) => c.kos));
      const top = eligible.filter((c) => c.kos === maxKos);
      return pickRandom(top, rng);
    }
    case "random":
    default:
      return pickRandom(eligible, rng);
  }
}
function pickRandom(pool, rng) {
  const idx = Math.floor(rng() * pool.length);
  const safe = Math.min(idx, pool.length - 1);
  return pool[safe].userId;
}
function applyGarbageToGrid(grid, lines, rng) {
  if (lines <= 0) return grid;
  const cap = Math.min(lines, GRID_ROWS);
  const surviving = grid.slice(cap);
  const newRows = [];
  for (let r = 0; r < cap; r++) {
    const row = [];
    for (let c = 0; c < GRID_COLS; c++) {
      const value = MIN_VALUE + Math.floor(rng() * (MAX_VALUE - MIN_VALUE + 1));
      row.push({ value, id: `g${r}c${c}-${Math.floor(rng() * 4294967295).toString(16)}` });
    }
    newRows.push(row);
  }
  return [...surviving, ...newRows];
}
function makeGarbageRng(matchSeed, attackIndex) {
  const combined = (matchSeed ^ attackIndex * 2654435761) >>> 0;
  return mulberry32(combined);
}
export {
  CELL_COUNT,
  GRID_COLS,
  GRID_ROWS,
  MAX_VALUE,
  MIN_VALUE,
  TARGET_SUM,
  TIMER_MS,
  TIMER_SECONDS,
  applyGarbageToGrid,
  applyMove,
  createGame,
  dateToSeed,
  garbageLinesFromCleared,
  generateBoard,
  isExpired,
  isRectValid,
  makeGarbageRng,
  mulberry32,
  pickGarbageTarget,
  rectSum,
  replay
};
